import React from "react";
// Import from the specific util modules (not the @/utils barrel) to avoid the
// barrel -> common.ts -> @/common -> Header circular init (URL undefined).
import { buildRelatedNewsSlug } from "@/utils/relatedNews";
import { Pagination } from "./Pagination";
import { defaultImagePath } from "@/common";
import styles from "./styles.module.scss";

export type RelatedNewsListItem = {
  title: string;
  summary: string;
  url: string;
  thumbnail?: string;
  // Editorial taxonomy shown in the chip ("Commentary", "Editor's View", ...).
  // Sparsely populated upstream, so it is optional and the chip is simply omitted
  // when absent rather than filled with a placeholder.
  category?: string | null;
  author?: string | null;
  date?: string;
};

type Props = {
  institutionName: string;
  baseSlug: string;
  backHref: string;
  page: number;
  pageCount: number;
  items: RelatedNewsListItem[];
};

export const RelatedNewsListing = ({
  institutionName,
  baseSlug,
  backHref,
  page,
  pageCount,
  items,
}: Props) => {
  // Relative, not absolute: every Content Hub page is flat in the same
  // /institution-profiles/ directory (assetPrefix "."), so a relative href keeps the
  // visitor on the host they arrived on, while a crawler that came in from the
  // sitemap resolves these against the canonical host instead of the navigation one
  // (WEB-27455 QA #3). This stays host-agnostic by design, which is why WEB-33210
  // could move the navigation host without touching it.
  const buildHref = (p: number) => `${buildRelatedNewsSlug(baseSlug, p)}.html`;

  return (
    <div className={styles.listing}>
      <a className={styles.back} href={backHref}>
        &larr; Back to {institutionName} profile
      </a>
      <h2 className={styles.heading}>{institutionName} related stories</h2>
      <p className={styles.subheading}>All articles and analysis</p>

      {items.length === 0 ? (
        <p className={styles.empty}>
          There are currently no related news articles for {institutionName}.
        </p>
      ) : (
        <ul className={styles.grid}>
          {items.map((item, i) => (
            <li key={item.url + i} className={styles.row}>
              <img
                className={styles.thumb}
                src={item.thumbnail || defaultImagePath}
                alt=""
                height={72}
                width={72}
                loading="lazy"
              />
              <div className={styles.body}>
                <a className={styles.title} href={item.url} target="_blank" rel="noreferrer">
                  {item.title}
                </a>
                <div className={styles.meta}>
                  {item.category && <span className={styles.tag}>{item.category}</span>}
                  {item.author && <span className={styles.author}>{item.author}</span>}
                  {item.date && (
                    <span className={styles.date}>
                      {item.author ? "- " : ""}
                      {item.date}
                    </span>
                  )}
                </div>
                {/* Related-news records often carry an empty summary — don't leave a gap. */}
                {item.summary && <p className={styles.summary}>{item.summary}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Pagination currentPage={page} pageCount={pageCount} buildHref={buildHref} />
    </div>
  );
};
